import { AlertErrorCircle, Document, Template, XClear } from '@/assets/icons'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { ContentViewerActions } from '@/content-viewer/store/slice'
import { hasViewableMarkdownFile, isPdfFile } from '@/routes/dashboard/files/utils/files-utils'
import { extractFileNameFromFullPath } from '@/util/filepath-utils'
import { useViewFile } from '@/routes/dashboard/files/hooks/useViewFile'
import { selectFileUploadTasksWithKeys } from '@/store/selectors/file-upload-tasks-reducers.selector'
import { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { Tooltip } from '@mui/material'
import { clsx } from 'clsx'
import { FileProcessingStatus } from '@/store/slices/file-upload-tasks.slice'
import { useEffect, useRef, useState } from 'react'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationShortMs } from '@/constants/constants-ui'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { ViewerOpenContextKind } from '@/content-viewer/store/schemas'

type FilesPillsProps = {
  file: [string, FileProcessingStatus]
  onRemoveFile: (file: string) => void
  isTemplate?: boolean
  conversationId?: string
}

export default function FilePill({ file, onRemoveFile, isTemplate = false, conversationId }: FilesPillsProps) {
  const dispatch = useAppDispatch()
  const viewFile = useViewFile()
  const { trackEvent } = useAnalytics()

  // State
  const [isLoading, setIsLoading] = useState(false)

  // Use ref to track if file was opened (for cleanup)
  const openedFileRef = useRef(false)

  // Extract file information from props
  const [fileKey, fileStatus] = file
  const fileUploadedInformation = useAppSelector((state: RootState) => selectFileUploadTasksWithKeys(state, { keys: [fileKey] }))

  // Derived values
  const fileName = fileUploadedInformation[fileKey]?.filePath ?? extractFileNameFromFullPath(fileKey)
  const filePath = fileUploadedInformation[fileKey]?.sourcePath ?? fileKey
  const processSuccess = fileStatus === FileProcessingStatus.SUCCESS
  const isError = fileStatus === FileProcessingStatus.ERROR
  const disableCrossButton = !processSuccess && !isError
  const errorMessage = isError ? fileUploadedInformation[fileKey]?.errorMessage ?? 'There was an error processing this file.' : null

  // Check if preview is supported based on file path
  const isPreviewSupported = isPdfFile(filePath) || (processSuccess && hasViewableMarkdownFile(filePath))

  // Event handlers
  const handleClick = async () => {
    if (!isPreviewSupported || isLoading) return

    setIsLoading(true)
    try {
      await viewFile(filePath, { context: { kind: ViewerOpenContextKind.SELECTED_FILE, conversationId } })
      openedFileRef.current = true

      // Track file preview opened
      trackEvent(AnalyticsEvent.AssistantFilePillPreviewOpened, conversationId ? { conversationId } : {})
    } catch (error) {
      console.error('Error fetching metadata:', error)
      dispatch(
        openGlobalToast({
          type: GlobalToastType.ERROR,
          message: 'There was an error previewing this file. Please try again.',
          durationMs: kToastDurationShortMs,
        })
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onRemoveFile(fileKey)
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (openedFileRef.current) {
        dispatch(ContentViewerActions.clearContentViewer())
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Display elements for grid
  function DynamicIcon(): JSX.Element {
    let icon: JSX.Element

    if (isError) {
      icon = <AlertErrorCircle className="w-4 h-4 text-red-700" />
    } else if (!processSuccess) {
      icon = <CircularProgressContinuousSized size={12} thickness={7} />
    } else if (isTemplate) {
      icon = <Template className="w-[18px] h-[18px]" />
    } else {
      icon = <Document className="w-[18px] h-[18px] text-brand-neutral-800" />
    }

    return <div className="inline-flex items-center justify-center">{icon}</div>
  }

  return (
    <div
      className={clsx(
        'text-xs rounded flex items-center px-2 py-1 gap-1.5 min-h-[26px]',
        isError ? 'bg-red-100' : isTemplate ? 'bg-brand-neutral-300 hocus:bg-brand-neutral-400' : 'bg-brand-neutral-50 hocus:bg-brand-neutral-100',
        !isError && !isTemplate ? 'ring-1 ring-inset ring-brand-neutral-200' : '',
        isPreviewSupported ? 'cursor-pointer' : 'cursor-not-allowed'
      )}
      onClick={handleClick}
    >
      <DynamicIcon />
      <Tooltip title={fileName + (errorMessage ? ` (${errorMessage})` : '')} placement="top">
        <div
          className={clsx(
            'flex-1 min-w-0 truncate whitespace-nowrap overflow-hidden font-medium text-wrap line-clamp-1 break-all',
            isTemplate ? '' : 'text-brand-neutral-800',
            isError ? 'text-red-950' : ''
          )}
        >
          {fileName}
        </div>
      </Tooltip>
      <button disabled={disableCrossButton} className="self-center" onClick={handleRemoveClick}>
        {!disableCrossButton && (
          <XClear className={clsx('w-[18px] h-[18px] cursor-pointer', isError ? 'text-red-950' : isTemplate ? '' : 'text-brand-neutral-600')} />
        )}
      </button>
    </div>
  )
}
